/**
 * Developer switch: send History's queries to StarRocks or MotherDuck by name, to compare them.
 *
 * Wherever the debug tools are on (dev and staging), the debug sidebar's History engine switch
 * (Other), or
 *
 *   sessionStorage.setItem('ett:history-engine', 'starrocks'); location.reload()
 *
 * makes every `/history/` request carry `X-History-Engine: starrocks` (or `motherduck`;
 * base-query.ts). The API answers from StarRocks where it can (never in production; route by
 * route as they move) and, either way, says which engine ran it in `meta.engine` and how long the
 * database took in a `Server-Timing` header. Each answer's timing is kept here for the sidebar.
 * Unset, requests go out as always and nothing is timed. Session storage, so it dies with the tab.
 */
import { debugToolsEnabled } from '@/ett-debug-tools/debug-enabled'

export const HISTORY_ENGINE_KEY = 'ett:history-engine'
export const HISTORY_ENGINE_HEADER = 'X-History-Engine'
export type HistoryEngine = 'motherduck' | 'starrocks'

const ENGINES: readonly string[] = ['motherduck', 'starrocks']

function sessionStore(): Storage | null {
  return typeof window === 'undefined' ? null : window.sessionStorage
}

/** The engine asked for, or null. `enabled` is a parameter so the guard is testable. */
export function readDevHistoryEngine(
  storage?: Pick<Storage, 'getItem'> | null,
  enabled: boolean = debugToolsEnabled,
): HistoryEngine | null {
  if (!enabled) return null
  try {
    const raw = (storage ?? sessionStore())?.getItem(HISTORY_ENGINE_KEY) ?? null
    return raw !== null && ENGINES.includes(raw) ? (raw as HistoryEngine) : null
  } catch {
    // A browser with site data blocked throws on access: no override.
    return null
  }
}

export function writeDevHistoryEngine(
  engine: HistoryEngine | null,
  storage?: Pick<Storage, 'setItem' | 'removeItem'> | null,
): void {
  const target = storage ?? sessionStore()
  try {
    if (engine) target?.setItem(HISTORY_ENGINE_KEY, engine)
    else target?.removeItem(HISTORY_ENGINE_KEY)
  } catch {
    // Blocked storage: nothing to switch.
  }
}

export function isHistoryUrl(url: string): boolean {
  return /\/v2\/history\//.test(url)
}

export interface ServerTiming {
  engine: string | null
  dbMs: number | null
  cached: boolean
}

/** `db;dur=12.3;desc="starrocks"` or `cache;desc="motherduck"` (services/history_engine.py). */
export function parseServerTiming(value: string | null): ServerTiming {
  if (!value) return { engine: null, dbMs: null, cached: false }
  const desc = /desc="([^"]*)"/.exec(value)?.[1] ?? null
  const dur = /dur=([\d.]+)/.exec(value)?.[1]
  return {
    engine: desc,
    dbMs: dur !== undefined ? Number(dur) : null,
    cached: value.trim().startsWith('cache'),
  }
}

export interface HistoryTiming extends ServerTiming {
  at: number
  path: string
  asked: HistoryEngine
  totalMs: number
  status: number
}

const MAX_TIMINGS = 50
let timings: HistoryTiming[] = []
const listeners = new Set<() => void>()

export function recordHistoryTiming(timing: HistoryTiming): void {
  timings = [timing, ...timings].slice(0, MAX_TIMINGS)
  listeners.forEach((listener) => listener())
}

/** Newest first. A new array per record, so it suits useSyncExternalStore. */
export function getHistoryTimings(): readonly HistoryTiming[] {
  return timings
}

export function subscribeHistoryTimings(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function __clearHistoryTimingsForTests(): void {
  timings = []
  listeners.clear()
}
